import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ContentBox from '../ContentBox';
import { useStaffPanel } from './StaffPanelContext';
import { visibleStaffNav, activeStaffKey } from './staffNav';
import { getMaintenanceStatus } from '../../services/api';

const UPLOAD_MODE_STYLES = {
    all:        { inactive: 'bg-green-100 text-green-900 hover:bg-green-200', active: 'bg-green-600 text-white' },
    staff_only: { inactive: 'bg-orange-100 text-orange-900 hover:bg-orange-200', active: 'bg-orange-500 text-white' },
    offline:    { inactive: 'bg-red-100 text-red-900 hover:bg-red-200', active: 'bg-red-600 text-white' },
};

function StaffSectionLayout({ children }) {
    const location = useLocation();
    const { userData } = useStaffPanel();
    const sectionKey = activeStaffKey(location.pathname);
    const [cloudUploadMode, setCloudUploadMode] = useState('all');

    const section = useMemo(
        () => visibleStaffNav(userData).find(item => item.key === sectionKey),
        [userData, sectionKey]
    );

    const needsUploadMode = useMemo(
        () => !!section?.groups?.some(group => group.links.some(link => link.uploadMode)),
        [section]
    );

    useEffect(() => {
        if (!needsUploadMode) return;

        let active = true;
        getMaintenanceStatus()
            .then(r => { if (active && r?.cloudUploadMode) setCloudUploadMode(r.cloudUploadMode); })
            .catch(() => {});

        return () => { active = false; };
    }, [needsUploadMode, location.pathname]);

    if (!section || !section.groups || section.groups.length === 0) {
        return <>{children}</>;
    }

    const isAdminSection = section.key === 'admin';

    const linkClasses = (link, isActive) => {
        if (link.uploadMode) {
            const modeStyle = UPLOAD_MODE_STYLES[cloudUploadMode] || UPLOAD_MODE_STYLES.all;
            return isActive ? modeStyle.active : modeStyle.inactive;
        }
        return isActive ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300';
    };

    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6">
            <div className="flex flex-col gap-4 lg:col-span-3">
                {section.groups.map((group, groupIndex) => (
                    <ContentBox
                        key={groupIndex}
                        theme="default"
                        tint={isAdminSection ? 'red' : 'lightskyblue'}
                        header={group.header || section.label}
                    >
                        <div className="flex flex-col gap-2">
                            {group.links.map(link => {
                                const isActive = location.pathname === link.path;
                                return (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        className={`font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-3 py-2 rounded transition-all ${linkClasses(link, isActive)}`}
                                        style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                                    >
                                        {link.label}
                                    </Link>
                                );
                            })}
                        </div>
                    </ContentBox>
                ))}
            </div>

            <div className="lg:col-span-9">
                {children}
            </div>
        </div>
    );
}

export default StaffSectionLayout;
